/**
 * ABC Classification Service
 * TASK-010: Implements Pareto-based product classification per CONTEXT.md Section 2.1
 *
 * Features:
 * - Automatic ABC classification based on sales value
 * - Pareto principle (80/20) implementation
 * - Configurable thresholds per tenant
 * - Historical tracking of classification changes
 * - Dashboard-ready summaries
 */

import { supabase } from './supabase';
import { logErrorWithTrace } from './api';
import type {
    ABCClass,
    ABCClassificationConfig,
    ABCClassificationSummary,
    ABCClassificationHistory,
    ABCClassificationResult,
    ABCClassChange,
    ProductWithABC,
    DateRange,
    PaginatedResult,
} from '../types';

// ============================================
// TYPES
// ============================================

export interface ABCPaginationParams {
    page?: number;
    pageSize?: number;
    sortBy?: 'sku' | 'name' | 'sales_value_30d' | 'abc_class' | 'sales_velocity_rank';
    sortOrder?: 'asc' | 'desc';
}

export interface ABCFilterParams {
    abcClass?: ABCClass | ABCClass[];
    category?: string;
    supplierId?: string;
    search?: string;
    minSalesValue?: number;
}

export interface ClassificationOptions {
    periodDays?: number;
    minSalesValue?: number;
    minTransactions?: number;
    classAThreshold?: number;  // Default 80%
    classBThreshold?: number;  // Default 95% cumulative (80 + 15)
}

// ============================================
// DB ROW TYPES (columns not yet in generated DB types)
// ============================================

/** Extended product row shape including ABC classification columns */
interface ProductRowWithABC {
    id: string;
    tenant_id: string;
    sku: string;
    name: string;
    category: string | null;
    unit_cost: number | null;
    unit_price: number | null;
    reorder_point: number | null;
    lead_time_days: number | null;
    abc_class?: string | null;
    sales_value_30d?: number | null;
    sales_velocity_rank?: number | null;
    cumulative_sales_pct?: number | null;
    abc_calculated_at?: string | null;
    supplier_name?: string | null;
    supplier_grade?: string | null;
}

/** ABC classification history row with joined product data */
interface ABCHistoryRowWithProduct {
    tenant_id: string;
    product_id: string;
    abc_class: string;
    previous_class: string | null;
    sales_value: number | null;
    cumulative_percentage: number | null;
    sales_velocity_rank: number | null;
    classification_date: string;
    products?: {
        sku: string | null;
        name: string | null;
        category: string | null;
    } | null;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Map database row to ProductWithABC type
 */
function mapDbToProductWithABC(row: any): ProductWithABC {
    return {
        id: row.id,
        tenantId: row.tenant_id,
        sku: row.sku,
        name: row.name,
        category: row.category,
        abcClass: row.abc_class,
        salesValue30d: row.sales_value_30d ?? 0,
        salesVelocityRank: row.sales_velocity_rank,
        cumulativeSalesPct: row.cumulative_sales_pct,
        abcCalculatedAt: row.abc_calculated_at,
        unitCost: row.unit_cost,
        unitPrice: row.unit_price,
        reorderPoint: row.reorder_point ?? 0,
        leadTimeDays: row.lead_time_days ?? 7,
        totalStock: row.total_stock ?? 0,
        inventoryValue: row.inventory_value ?? 0,
        supplierName: row.supplier_name,
        supplierGrade: row.supplier_grade,
    };
}

/**
 * Map database row to ABCClassificationConfig type
 */
function mapDbToConfig(row: any): ABCClassificationConfig {
    return {
        id: row.id,
        tenantId: row.tenant_id,
        classASalesPct: row.class_a_sales_pct ?? 80,
        classBSalesPct: row.class_b_sales_pct ?? 15,
        classCSalesPct: row.class_c_sales_pct ?? 5,
        analysisPeriodDays: row.analysis_period_days ?? 30,
        minSalesValue: row.min_sales_value ?? 0,
        minTransactions: row.min_transactions ?? 0,
        autoClassifyEnabled: row.auto_classify_enabled ?? true,
        autoClassifyFrequency: row.auto_classify_frequency ?? 'weekly',
        lastAutoClassifyAt: row.last_auto_classify_at,
        notifyOnClassChange: row.notify_on_class_change ?? true,
        notifyAToB: row.notify_a_to_b ?? true,
        notifyBToA: row.notify_b_to_a ?? true,
        createdAt: row.created_at,
        updatedAt: row.updated_at,
    };
}

/**
 * Map database row to ABCClassificationSummary type
 */
function mapDbToSummary(row: any): ABCClassificationSummary {
    return {
        id: row.id,
        tenantId: row.tenant_id,
        summaryDate: row.summary_date,
        classAProductCount: row.class_a_product_count ?? 0,
        classAProductPct: row.class_a_product_pct ?? 0,
        classASalesValue: row.class_a_sales_value ?? 0,
        classASalesPct: row.class_a_sales_pct ?? 0,
        classAInventoryValue: row.class_a_inventory_value ?? 0,
        classBProductCount: row.class_b_product_count ?? 0,
        classBProductPct: row.class_b_product_pct ?? 0,
        classBSalesValue: row.class_b_sales_value ?? 0,
        classBSalesPct: row.class_b_sales_pct ?? 0,
        classBInventoryValue: row.class_b_inventory_value ?? 0,
        classCProductCount: row.class_c_product_count ?? 0,
        classCProductPct: row.class_c_product_pct ?? 0,
        classCSalesValue: row.class_c_sales_value ?? 0,
        classCSalesPct: row.class_c_sales_pct ?? 0,
        classCInventoryValue: row.class_c_inventory_value ?? 0,
        unclassifiedCount: row.unclassified_count ?? 0,
        totalProducts: row.total_products ?? 0,
        totalSalesValue: row.total_sales_value ?? 0,
        totalInventoryValue: row.total_inventory_value ?? 0,
        createdAt: row.created_at,
    };
}

/**
 * Map database row to ABCClassificationHistory type
 */
function mapDbToHistory(row: any): ABCClassificationHistory {
    return {
        id: row.id,
        tenantId: row.tenant_id,
        productId: row.product_id,
        abcClass: row.abc_class,
        previousClass: row.previous_class,
        salesValue: row.sales_value ?? 0,
        cumulativePercentage: row.cumulative_percentage ?? 0,
        salesVelocityRank: row.sales_velocity_rank ?? 0,
        classificationDate: row.classification_date,
        periodStart: row.period_start,
        periodEnd: row.period_end,
        totalProductsClassified: row.total_products_classified ?? 0,
        classACutoffPct: row.class_a_cutoff_pct ?? 80,
        classBCutoffPct: row.class_b_cutoff_pct ?? 95,
        createdAt: row.created_at,
    };
}

// ============================================
// ABC CLASSIFICATION SERVICE
// ============================================

export const abcClassificationService = {
    // ============================================
    // CLASSIFICATION OPERATIONS
    // ============================================

    /**
     * Run ABC classification for a tenant
     * Implements Pareto principle per CONTEXT.md Section 2.1
     */
    async classifyProducts(
        tenantId: string,
        options?: ClassificationOptions
    ): Promise<ABCClassificationResult> {
        try {
            // Get tenant config or use defaults
            const config = await this.getConfig(tenantId);

            const periodDays = options?.periodDays ?? config?.analysisPeriodDays ?? 30;
            const minSalesValue = options?.minSalesValue ?? config?.minSalesValue ?? 0;
            const classAThreshold = options?.classAThreshold ?? config?.classASalesPct ?? 80;
            const classBThreshold = options?.classBThreshold ??
                (config?.classASalesPct ?? 80) + (config?.classBSalesPct ?? 15);

            const periodStart = new Date();
            periodStart.setDate(periodStart.getDate() - periodDays);
            const periodStartStr = periodStart.toISOString().split('T')[0];
            const periodEndStr = new Date().toISOString().split('T')[0];

            // Step 1: Calculate 30-day sales value for each product
            // Using transactions table
            const { data: salesData, error: salesError } = await supabase
                .from('transactions')
                .select('product_id, quantity, total_amount, type')
                .eq('tenant_id', tenantId)
                .gte('created_at', periodStart.toISOString())
                .in('type', ['SALE', 'RETURN']);

            if (salesError) throw salesError;

            // Aggregate sales by product
            const productSales: Map<string, number> = new Map();
            (salesData || []).forEach(tx => {
                const currentValue = productSales.get(tx.product_id) || 0;
                const txValue = tx.type === 'SALE'
                    ? (tx.total_amount || 0)
                    : -(tx.total_amount || 0);
                productSales.set(tx.product_id, currentValue + txValue);
            });

            // Step 2: Get all products
            // Note: abc_class column requires migration - using type assertion
            const { data: products, error: prodError } = await supabase
                .from('products')
                .select('id')
                .eq('tenant_id', tenantId);

            if (prodError) throw prodError;

            // Filter and sort by sales value
            const productsWithSales = (products || [])
                .map(p => ({
                    id: p.id,
                    previousClass: undefined as ABCClass | undefined,
                    salesValue: productSales.get(p.id) || 0,
                }))
                .filter(p => p.salesValue >= minSalesValue)
                .sort((a, b) => b.salesValue - a.salesValue);

            // Step 3: Calculate total sales
            const totalSales = productsWithSales.reduce((sum, p) => sum + p.salesValue, 0);

            if (totalSales === 0) {
                return {
                    productsClassified: 0,
                    classACount: 0,
                    classBCount: 0,
                    classCCount: 0,
                    executedAt: new Date().toISOString(),
                };
            }

            // Step 4: Assign ABC classes based on cumulative percentage
            let cumulativeSales = 0;
            const classifications: {
                productId: string;
                previousClass?: ABCClass;
                newClass: ABCClass;
                salesValue: number;
                cumulativePct: number;
                rank: number;
            }[] = [];

            productsWithSales.forEach((product, index) => {
                cumulativeSales += product.salesValue;
                const cumulativePct = (cumulativeSales / totalSales) * 100;

                let newClass: ABCClass;
                if (cumulativePct <= classAThreshold) {
                    newClass = 'A';
                } else if (cumulativePct <= classBThreshold) {
                    newClass = 'B';
                } else {
                    newClass = 'C';
                }

                classifications.push({
                    productId: product.id,
                    previousClass: product.previousClass,
                    newClass,
                    salesValue: product.salesValue,
                    cumulativePct,
                    rank: index + 1,
                });
            });

            // Step 5: Update products
            // Note: These columns require migration - using type assertion
            for (const cls of classifications) {
                await (supabase
                    .from('products'))
                    .update({
                        abc_class: cls.newClass,
                        abc_calculated_at: new Date().toISOString(),
                        sales_value_30d: cls.salesValue,
                        sales_velocity_rank: cls.rank,
                        cumulative_sales_pct: cls.cumulativePct,
                    })
                    .eq('id', cls.productId);
            }

            // Step 6: Record history
            const historyRecords = classifications.map(cls => ({
                tenant_id: tenantId,
                product_id: cls.productId,
                abc_class: cls.newClass,
                previous_class: cls.previousClass,
                sales_value: cls.salesValue,
                cumulative_percentage: cls.cumulativePct,
                sales_velocity_rank: cls.rank,
                classification_date: new Date().toISOString().split('T')[0],
                period_start: periodStartStr,
                period_end: periodEndStr,
                total_products_classified: classifications.length,
                class_a_cutoff_pct: classAThreshold,
                class_b_cutoff_pct: classBThreshold,
            }));

            // Note: abc_classification_history table requires migration - using type assertion
            await supabase.from('abc_classification_history').insert(historyRecords);

            // Step 7: Update summary
            const classACount = classifications.filter(c => c.newClass === 'A').length;
            const classBCount = classifications.filter(c => c.newClass === 'B').length;
            const classCCount = classifications.filter(c => c.newClass === 'C').length;

            const classASales = classifications
                .filter(c => c.newClass === 'A')
                .reduce((sum, c) => sum + c.salesValue, 0);
            const classBSales = classifications
                .filter(c => c.newClass === 'B')
                .reduce((sum, c) => sum + c.salesValue, 0);
            const classCSales = classifications
                .filter(c => c.newClass === 'C')
                .reduce((sum, c) => sum + c.salesValue, 0);

            // Note: abc_classification_summary table requires migration - using type assertion
            await supabase.from('abc_classification_summary').upsert({
                tenant_id: tenantId,
                summary_date: new Date().toISOString().split('T')[0],
                class_a_product_count: classACount,
                class_a_product_pct: classifications.length > 0 ? (classACount / classifications.length) * 100 : 0,
                class_a_sales_value: classASales,
                class_a_sales_pct: totalSales > 0 ? (classASales / totalSales) * 100 : 0,
                class_b_product_count: classBCount,
                class_b_product_pct: classifications.length > 0 ? (classBCount / classifications.length) * 100 : 0,
                class_b_sales_value: classBSales,
                class_b_sales_pct: totalSales > 0 ? (classBSales / totalSales) * 100 : 0,
                class_c_product_count: classCCount,
                class_c_product_pct: classifications.length > 0 ? (classCCount / classifications.length) * 100 : 0,
                class_c_sales_value: classCSales,
                class_c_sales_pct: totalSales > 0 ? (classCSales / totalSales) * 100 : 0,
                total_products: classifications.length,
                total_sales_value: totalSales,
            }, { onConflict: 'tenant_id,summary_date' });

            // Update config last run time
            // Note: abc_classification_config table requires migration - using type assertion
            if (config) {
                await supabase
                    .from('abc_classification_config')
                    .update({ last_auto_classify_at: new Date().toISOString() })
                    .eq('tenant_id', tenantId);
            }

            return {
                productsClassified: classifications.length,
                classACount,
                classBCount,
                classCCount,
                executedAt: new Date().toISOString(),
            };
        } catch (error) {
            logErrorWithTrace('abcClassificationService.classifyProducts', error);
            throw error;
        }
    },

    // ============================================
    // PRODUCT QUERIES
    // ============================================

    /**
     * Get products with ABC classification
     */
    async getProductsWithABC(
        tenantId: string,
        pagination?: ABCPaginationParams,
        filters?: ABCFilterParams
    ): Promise<PaginatedResult<ProductWithABC>> {
        try {
            const page = pagination?.page ?? 1;
            const pageSize = pagination?.pageSize ?? 20;
            const sortBy = pagination?.sortBy ?? 'sales_velocity_rank';
            const sortOrder = pagination?.sortOrder ?? 'asc';
            const offset = (page - 1) * pageSize;

            // Use the products_abc_view if available, otherwise query products directly
            // Note: Extended product columns and supplier relation require migration - using type assertion
            let query = (supabase
                .from('products'))
                .select('*', { count: 'exact' })
                .eq('tenant_id', tenantId);

            // Apply filters
            if (filters?.abcClass) {
                const classes = Array.isArray(filters.abcClass) ? filters.abcClass : [filters.abcClass];
                query = query.in('abc_class', classes);
            }

            if (filters?.category) {
                query = query.eq('category', filters.category);
            }

            if (filters?.supplierId) {
                query = query.eq('supplier_id', filters.supplierId);
            }

            if (filters?.search) {
                query = query.or(`name.ilike.%${filters.search}%,sku.ilike.%${filters.search}%`);
            }

            if (filters?.minSalesValue !== undefined) {
                query = query.gte('sales_value_30d', filters.minSalesValue);
            }

            // Apply sorting and pagination
            query = query
                .order(sortBy, { ascending: sortOrder === 'asc', nullsFirst: false })
                .range(offset, offset + pageSize - 1);

            const { data, error, count } = await query;

            if (error) throw error;

            return {
                data: ((data || []) as ProductRowWithABC[]).map(row => ({
                    id: row.id,
                    tenantId: row.tenant_id,
                    sku: row.sku,
                    name: row.name,
                    category: row.category,
                    abcClass: row.abc_class,
                    salesValue30d: row.sales_value_30d ?? 0,
                    salesVelocityRank: row.sales_velocity_rank,
                    cumulativeSalesPct: row.cumulative_sales_pct,
                    abcCalculatedAt: row.abc_calculated_at,
                    unitCost: row.unit_cost,
                    unitPrice: row.unit_price,
                    reorderPoint: row.reorder_point ?? 0,
                    leadTimeDays: row.lead_time_days ?? 7,
                    totalStock: 0, // Would need inventory join
                    inventoryValue: 0,
                    supplierName: row.supplier_name,
                    supplierGrade: row.supplier_grade,
                })),
                total: count ?? 0,
                page,
                pageSize,
                totalPages: Math.ceil((count ?? 0) / pageSize),
            };
        } catch (error) {
            logErrorWithTrace('abcClassificationService.getProductsWithABC', error);
            throw error;
        }
    },

    /**
     * Get products by ABC class
     */
    async getProductsByClass(
        tenantId: string,
        abcClass: ABCClass,
        limit?: number
    ): Promise<ProductWithABC[]> {
        try {
            // Note: Extended product columns require migration - using type assertion
            let query = (supabase
                .from('products'))
                .select('*')
                .eq('tenant_id', tenantId)
                .eq('abc_class', abcClass)
                .order('sales_velocity_rank', { ascending: true });

            if (limit) {
                query = query.limit(limit);
            }

            const { data, error } = await query;

            if (error) throw error;

            return ((data || []) as ProductRowWithABC[]).map(row => ({
                id: row.id,
                tenantId: row.tenant_id,
                sku: row.sku,
                name: row.name,
                category: row.category,
                abcClass: row.abc_class,
                salesValue30d: row.sales_value_30d ?? 0,
                salesVelocityRank: row.sales_velocity_rank,
                cumulativeSalesPct: row.cumulative_sales_pct,
                abcCalculatedAt: row.abc_calculated_at,
                unitCost: row.unit_cost,
                unitPrice: row.unit_price,
                reorderPoint: row.reorder_point ?? 0,
                leadTimeDays: row.lead_time_days ?? 7,
                totalStock: 0,
                inventoryValue: 0,
                supplierName: row.supplier_name,
                supplierGrade: row.supplier_grade,
            }));
        } catch (error) {
            logErrorWithTrace('abcClassificationService.getProductsByClass', error);
            throw error;
        }
    },

    // ============================================
    // CONFIGURATION
    // ============================================

    /**
     * Get tenant ABC configuration
     */
    async getConfig(tenantId: string): Promise<ABCClassificationConfig | null> {
        try {
            // Note: abc_classification_config table requires migration - using type assertion
            const { data, error } = await supabase
                .from('abc_classification_config')
                .select('*')
                .eq('tenant_id', tenantId)
                .single();

            if (error) {
                if (error.code === 'PGRST116') return null;
                throw error;
            }

            return data ? mapDbToConfig(data) : null;
        } catch (error) {
            logErrorWithTrace('abcClassificationService.getConfig', error);
            throw error;
        }
    },

    /**
     * Create or update tenant ABC configuration
     */
    async upsertConfig(
        tenantId: string,
        config: Partial<Omit<ABCClassificationConfig, 'id' | 'tenantId' | 'createdAt' | 'updatedAt'>>
    ): Promise<ABCClassificationConfig> {
        try {
            // Note: abc_classification_config table requires migration - using type assertion
            const { data, error } = await supabase
                .from('abc_classification_config')
                .upsert({
                    tenant_id: tenantId,
                    class_a_sales_pct: config.classASalesPct ?? 80,
                    class_b_sales_pct: config.classBSalesPct ?? 15,
                    class_c_sales_pct: config.classCSalesPct ?? 5,
                    analysis_period_days: config.analysisPeriodDays ?? 30,
                    min_sales_value: config.minSalesValue ?? 0,
                    min_transactions: config.minTransactions ?? 0,
                    auto_classify_enabled: config.autoClassifyEnabled ?? true,
                    auto_classify_frequency: config.autoClassifyFrequency ?? 'weekly',
                    notify_on_class_change: config.notifyOnClassChange ?? true,
                    notify_a_to_b: config.notifyAToB ?? true,
                    notify_b_to_a: config.notifyBToA ?? true,
                }, { onConflict: 'tenant_id' })
                .select()
                .single();

            if (error) throw error;

            return mapDbToConfig(data);
        } catch (error) {
            logErrorWithTrace('abcClassificationService.upsertConfig', error);
            throw error;
        }
    },

    // ============================================
    // SUMMARIES & ANALYTICS
    // ============================================

    /**
     * Get classification summary for a tenant
     */
    async getSummary(tenantId: string, date?: string): Promise<ABCClassificationSummary | null> {
        try {
            // Note: abc_classification_summary table requires migration - using type assertion
            let query = supabase
                .from('abc_classification_summary')
                .select('*')
                .eq('tenant_id', tenantId);

            if (date) {
                query = query.eq('summary_date', date);
            } else {
                query = query.order('summary_date', { ascending: false }).limit(1);
            }

            const { data, error } = await query.single();

            if (error) {
                if (error.code === 'PGRST116') return null;
                throw error;
            }

            return data ? mapDbToSummary(data) : null;
        } catch (error) {
            logErrorWithTrace('abcClassificationService.getSummary', error);
            throw error;
        }
    },

    /**
     * Get summary trend over time
     */
    async getSummaryTrend(
        tenantId: string,
        dateRange: DateRange
    ): Promise<ABCClassificationSummary[]> {
        try {
            // Note: abc_classification_summary table requires migration - using type assertion
            const { data, error } = await supabase
                .from('abc_classification_summary')
                .select('*')
                .eq('tenant_id', tenantId)
                .gte('summary_date', dateRange.start)
                .lte('summary_date', dateRange.end)
                .order('summary_date', { ascending: true });

            if (error) throw error;

            return (data || []).map(mapDbToSummary);
        } catch (error) {
            logErrorWithTrace('abcClassificationService.getSummaryTrend', error);
            throw error;
        }
    },

    // ============================================
    // HISTORY & CHANGES
    // ============================================

    /**
     * Get classification history for a product
     */
    async getProductHistory(
        productId: string,
        limit: number = 10
    ): Promise<ABCClassificationHistory[]> {
        try {
            // Note: abc_classification_history table requires migration - using type assertion
            const { data, error } = await supabase
                .from('abc_classification_history')
                .select('*')
                .eq('product_id', productId)
                .order('classification_date', { ascending: false })
                .limit(limit);

            if (error) throw error;

            return (data || []).map(mapDbToHistory);
        } catch (error) {
            logErrorWithTrace('abcClassificationService.getProductHistory', error);
            throw error;
        }
    },

    /**
     * Get products that changed class
     */
    async getClassChanges(
        tenantId: string,
        dateRange?: DateRange
    ): Promise<ABCClassChange[]> {
        try {
            // Note: abc_classification_history table requires migration - using type assertion
            let query = supabase
                .from('abc_classification_history')
                .select('*, products(sku, name, category)')
                .eq('tenant_id', tenantId)
                .not('previous_class', 'is', null);

            if (dateRange) {
                query = query
                    .gte('classification_date', dateRange.start)
                    .lte('classification_date', dateRange.end);
            }

            query = query.order('classification_date', { ascending: false });

            const { data, error } = await query;

            if (error) throw error;

            return ((data || []) as ABCHistoryRowWithProduct[])
                .filter(row => row.abc_class !== row.previous_class)
                .map(row => {
                    const prev = row.previous_class;
                    const curr = row.abc_class;
                    let changeType = '';

                    if (prev === 'A' && curr === 'B') changeType = 'Downgrade (A→B)';
                    else if (prev === 'A' && curr === 'C') changeType = 'Downgrade (A→C)';
                    else if (prev === 'B' && curr === 'A') changeType = 'Upgrade (B→A)';
                    else if (prev === 'B' && curr === 'C') changeType = 'Downgrade (B→C)';
                    else if (prev === 'C' && curr === 'A') changeType = 'Upgrade (C→A)';
                    else if (prev === 'C' && curr === 'B') changeType = 'Upgrade (C→B)';

                    return {
                        tenantId: row.tenant_id,
                        productId: row.product_id,
                        sku: row.products?.sku || '',
                        name: row.products?.name || '',
                        category: row.products?.category,
                        previousClass: row.previous_class,
                        newClass: row.abc_class,
                        salesValue: row.sales_value ?? 0,
                        classificationDate: row.classification_date,
                        changeType,
                    };
                });
        } catch (error) {
            logErrorWithTrace('abcClassificationService.getClassChanges', error);
            throw error;
        }
    },

    /**
     * Get downgrades (A→B, A→C, B→C)
     */
    async getDowngrades(tenantId: string, dateRange?: DateRange): Promise<ABCClassChange[]> {
        const changes = await this.getClassChanges(tenantId, dateRange);
        return changes.filter(c => c.changeType.startsWith('Downgrade'));
    },

    /**
     * Get upgrades (B→A, C→A, C→B)
     */
    async getUpgrades(tenantId: string, dateRange?: DateRange): Promise<ABCClassChange[]> {
        const changes = await this.getClassChanges(tenantId, dateRange);
        return changes.filter(c => c.changeType.startsWith('Upgrade'));
    },

    // ============================================
    // STATISTICS
    // ============================================

    /**
     * Get ABC statistics
     */
    async getStats(tenantId: string): Promise<{
        totalProducts: number;
        classifiedProducts: number;
        unclassifiedProducts: number;
        byClass: Record<ABCClass, { count: number; salesValue: number; pct: number }>;
        lastClassificationAt?: string;
    }> {
        try {
            // Note: Extended product columns require migration - using type assertion
            const { data: products, error } = await (supabase
                .from('products'))
                .select('abc_class, sales_value_30d, abc_calculated_at')
                .eq('tenant_id', tenantId);

            if (error) throw error;

            const stats = {
                totalProducts: products?.length ?? 0,
                classifiedProducts: 0,
                unclassifiedProducts: 0,
                byClass: {
                    A: { count: 0, salesValue: 0, pct: 0 },
                    B: { count: 0, salesValue: 0, pct: 0 },
                    C: { count: 0, salesValue: 0, pct: 0 },
                } as Record<ABCClass, { count: number; salesValue: number; pct: number }>,
                lastClassificationAt: undefined as string | undefined,
            };

            (products || []).forEach(p => {
                if (p.abc_class && ['A', 'B', 'C'].includes(p.abc_class)) {
                    stats.classifiedProducts++;
                    stats.byClass[p.abc_class as ABCClass].count++;
                    stats.byClass[p.abc_class as ABCClass].salesValue += p.sales_value_30d ?? 0;

                    if (p.abc_calculated_at) {
                        if (!stats.lastClassificationAt || p.abc_calculated_at > stats.lastClassificationAt) {
                            stats.lastClassificationAt = p.abc_calculated_at;
                        }
                    }
                } else {
                    stats.unclassifiedProducts++;
                }
            });

            // Calculate percentages
            if (stats.classifiedProducts > 0) {
                (['A', 'B', 'C'] as ABCClass[]).forEach(cls => {
                    stats.byClass[cls].pct = (stats.byClass[cls].count / stats.classifiedProducts) * 100;
                });
            }

            return stats;
        } catch (error) {
            logErrorWithTrace('abcClassificationService.getStats', error);
            throw error;
        }
    },
};

export default abcClassificationService;
